import { useRef, useCallback, ReactNode } from 'react';
import ReactDOMServer from 'react-dom/server';

import { PrintableQRCode } from 'src/Components/PrintTemplates/PrintableQRCode';

interface PrintQRCodeOptions {
  value: string;
  cmSize: number;
  topText?: string;
  bottomText?: string;
  size?: number;
  fgColor?: string;
  bgColor?: string;
  customContent?: ReactNode;
}

const usePrintQRCode = () => {
  const printFrameRef = useRef<HTMLIFrameElement | null>(null);

  const printQRCode = useCallback(
    ({ value, cmSize, topText = '', bottomText = '' }: PrintQRCodeOptions) => {
      try {
        // if iframe already exists, delete it
        if (printFrameRef.current && document.body.contains(printFrameRef.current)) {
          document.body.removeChild(printFrameRef.current);
          printFrameRef.current = null;
        }

        const iframe = document.createElement('iframe');
        // iframe.style.display = 'none';
        document.body.appendChild(iframe);
        printFrameRef.current = iframe;

        const printWindow = iframe.contentWindow;
        if (!printWindow) {
          throw new Error('Impossible to gain access to print window');
        }

        const printDocument = printWindow.document;

        const contentToRender = ReactDOMServer.renderToString(
          <PrintableQRCode url={value} topText={topText} bottomText={bottomText} cmSize={cmSize} />
        );

        const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${topText}</title>
          <!-- TODO: get ttf font from our asset folder -->
          <link rel="preconnect" href="https://fonts.googleapis.com">
          <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
          <link href="https://fonts.googleapis.com/css2?family=Public+Sans:wght@500&display=swap" rel="stylesheet">
          <style>
            @media print {
              body {
                print-color-adjust: exact;
                -webkit-print-color-adjust: exact;
              }
            }
          </style>
          <script>
           // Function to check if fonts are loaded
            function checkFontsLoaded() {
              // Create element with Public Sans Font
              const testElement = document.createElement('span');
              testElement.style.fontFamily = "'Public Sans', sans-serif";
              testElement.style.fontSize = '20px';
              testElement.style.visibility = 'hidden';
              testElement.textContent = 'Test Font Loading';
              document.body.appendChild(testElement);
              
              const startTime = performance.now();
              const maxWaitTime = 3000; // Timeout maximum de 3 secondes
              
              // Check if font is loaded
              function checkFont() {
                if (performance.now() - startTime > maxWaitTime) {
                  document.body.removeChild(testElement);
                  window.print();
                  return;
                }

                const computed = window.getComputedStyle(testElement);
sé
                if (computed.fontFamily.includes('Public Sans') || 
                    document.fonts && document.fonts.check && document.fonts.check("20px 'Public Sans'")) {
                  document.body.removeChild(testElement);
                  window.print();
                } else {
                  setTimeout(checkFont, 100);
                }
              }

              if (document.fonts && document.fonts.ready) {
                document.fonts.ready.then(function() {
                  document.body.removeChild(testElement);
                  window.print();
                }).catch(function() {
                  checkFont();
                });
              } else {
                checkFont();
              }
            }
            
            document.addEventListener('DOMContentLoaded', function() {
              var containerElements = document.querySelectorAll('[class*="container"]');
              containerElements.forEach(function(el) {
                el.classList.add('print-container');
              });

              var textElements = document.querySelectorAll('[class*="text"]');
              textElements.forEach(function(el) {
                el.classList.add('print-text');
              });
              
              checkFontsLoaded();
            });
            </script>
        </head>
        <body>
          ${contentToRender}
        </body>
        </html>
      `;

        printDocument.open();
        printDocument.write(printContent);
        printDocument.close();

        const cleanup = () => {
          setTimeout(() => {
            if (printFrameRef.current && document.body.contains(printFrameRef.current)) {
              document.body.removeChild(printFrameRef.current);
              printFrameRef.current = null;
            }
          }, 2000);
        };

        printWindow.addEventListener('afterprint', cleanup);

        setTimeout(cleanup, 10000);
      } catch (error) {
        console.error('Error while printing : ', error);
      }
    },
    []
  );

  return {
    printQRCode,
  };
};

export default usePrintQRCode;
